import React from 'react'
import { Teacher } from '../providers/context'
import { TextInput, CharcoalInput } from '../components/form/textInput'
import { IconDownload, IconSearch } from '../icons/library'
import { ThreePanelLayout } from '../layouts/threePanel'
import { ListButton, ListButtonCheckbox } from '../components/simple/listButton'
import { formatDateRange, SimpleDateRange, SimpleDateRangePicker } from '../components/form/datePicker'
import { Spacer } from '../components/simple/divider'
import { ControlledModal, ModalTitle } from '../layouts/components/modal'
import { ExportWidget } from './dashboard/widgets'
import { LocalTable } from '../components/table'
import { ColumnDef } from '@tanstack/react-table'
import { LineChart, LineChartData, SparklineChart, SparklineChartData } from '../components/charts/lineChart'
import { useRequest } from '../request'
import { Tooltip } from '../components/simple/tooltip'
import { linkTo } from '../utils'
import { Checkbox } from '../components/form/checkbox'
import { Loader } from '../components/simple/loader'

type SchoolStats = {
  name: string
  id: number
  counts: {
    teachers: number
    students: number
    guardians: number
  }
  teacherMessages: {
    sentByTeachers: number
    perTeacher: number
    percentTeachersSending: number
  }
  familyMessages: {
    sentByFamilies: number
    perStudent: number
  }
  familiesReceivingMessages: string
  familiesSendingMessages: string
  loginPercentages: {
    teachers: string
    students: string
    guardians: string
    downloadLink: string
  }
  usageGraph: SparklineChartData
  usageMetric: number
}

export const DistrictStatsPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const [search, setSearch] = React.useState('')
  const [onlyTeachersWithClasses, setOnlyTeachersWithClasses] = React.useState(true)
  const [groupAnnouncements, setGroupAnnouncements] = React.useState(false)
  const [range, setRange] = React.useState<SimpleDateRange>('month')
  const [showExportModal, setShowExportModal] = React.useState(false)

  const [unselectedSchools, setUnselectedSchools] = React.useState(new Set<number>())

  const columns: ColumnDef<SchoolStats>[] = React.useMemo(
    () => [
      {
        header: 'School',
        accessorKey: 'name',
        cell: ({ row }) => (
          <div className={`flex items-center gap-2`}>
            {row.original.name}
            <Tooltip text="Include this school in the charts below">
              <Checkbox
                checked={!unselectedSchools.has(row.original.id)}
                onChecked={(c) =>
                  setUnselectedSchools((s) => {
                    const newSchools = new Set(s)
                    if (c) newSchools.delete(row.original.id)
                    else newSchools.add(row.original.id)
                    return newSchools
                  })
                }
              />
            </Tooltip>
          </div>
        ),
      },
      {
        header: () => (
          <div className="text-wrap text-left">
            Count<span className="!font-normal"> teachers | students | parents</span>
          </div>
        ),
        accessorFn: (row) => `${row.counts.teachers} | ${row.counts.students} | ${row.counts.guardians}`,
        cell: ({ row }) => (
          <div className="text-nowrap">
            {row.original.counts.teachers} | {row.original.counts.students} | {row.original.counts.guardians}
          </div>
        ),
        id: 'count',
      },
      {
        header: () => (
          <Tooltip text="Total messages sent by teachers, divided by the number of teachers">
            <div className="text-wrap text-left">
              Messages sent by teachers<span className="!font-normal"> | per teacher | % teachers sending</span>
            </div>
          </Tooltip>
        ),
        accessorFn: (row) =>
          `${row.teacherMessages.sentByTeachers} | ${row.teacherMessages.perTeacher} | ${row.teacherMessages.percentTeachersSending}%`,
        cell: ({ row }) => (
          <div className="text-nowrap">
            {row.original.teacherMessages.sentByTeachers} | {row.original.teacherMessages.perTeacher} |{' '}
            {row.original.teacherMessages.percentTeachersSending}%
          </div>
        ),
        id: 'teacher messages',
      },
      {
        header: () => (
          <Tooltip text="Total messages sent by students and guardians, divided by the number of students.">
            <div className="text-wrap text-left">
              Messages sent by families<span className="!font-normal"> per student</span>
            </div>
          </Tooltip>
        ),
        accessorFn: (row) => `${row.familyMessages.sentByFamilies} | ${row.familyMessages.perStudent}`,
        cell: ({ row }) => (
          <div className="text-nowrap">
            {row.original.familyMessages.sentByFamilies} | {row.original.familyMessages.perStudent}
          </div>
        ),
        id: 'family messages',
      },
      {
        header: () => (
          <div className="text-wrap text-left">
            Families receiving messages<span className="!font-normal"> (%)</span>
          </div>
        ),
        accessorKey: 'familiesReceivingMessages',
      },
      {
        header: () => (
          <div className="text-wrap text-left">
            Families sending messages<span className="!font-normal"> (%)</span>
          </div>
        ),
        accessorKey: 'familiesSendingMessages',
      },
      {
        header: () => (
          <div className="text-wrap text-left">
            Logins<span className="!font-normal"> teachers | students | parents (%)</span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-nowrap">
            {row.original.loginPercentages.teachers} | {row.original.loginPercentages.students} |{' '}
            {row.original.loginPercentages.guardians}
            <IconDownload
              className="cursor-pointer text-logo"
              tooltip="Download logins"
              onClick={linkTo(row.original.loginPercentages.downloadLink)}
            />
          </div>
        ),
        id: 'logins',
      },
      {
        header: 'Usage graph',
        cell: ({ row }) => <SparklineChart data={row.original.usageGraph} />,
      },
      {
        header: () => (
          <Tooltip text="Score indicating overall usage at this school in the last week. Higher is better.">
            <div>
              Usage
              <br />
              metric
            </div>
          </Tooltip>
        ),
        accessorKey: 'usageMetric',
      },
    ],
    [unselectedSchools, setUnselectedSchools]
  )

  const { data: tableData, loading: tableLoading } = useRequest<SchoolStats[]>(
    '/frontend_api/getDistrictStats',
    React.useMemo(
      () => ({
        onlyTeachersWithClasses,
        groupAnnouncements,
        range: formatDateRange(range),
      }),
      [onlyTeachersWithClasses, groupAnnouncements, range]
    )
  )

  const { data: chartData, loading: chartLoading } = useRequest<{ usage: LineChartData; inout: LineChartData }>(
    '/frontend_api/getDistrictStatsChart',
    React.useMemo(
      () => ({
        unselectedSchoolIDs: unselectedSchools,
        range: formatDateRange(range),
        groupAnnouncements,
      }),
      [unselectedSchools, range, groupAnnouncements]
    )
  )

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <TextInput
            className={`rounded-full outline outline-1 outline-charcoal-text ${CharcoalInput}`}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={<IconSearch />}
          />
          <div className="ml-2 flex items-center justify-between gap-2">
            Time Range:
            <SimpleDateRangePicker
              className="!border-none bg-charcoal-active !ring-0"
              range={range}
              onRangeChange={setRange}
            />
          </div>
          <ListButton onClick={() => setOnlyTeachersWithClasses((c) => !c)}>
            Only show teachers with classes / groups
            <ListButtonCheckbox checked={onlyTeachersWithClasses} />
          </ListButton>
          <ListButton onClick={() => setGroupAnnouncements((c) => !c)}>
            Group Announcements
            <ListButtonCheckbox checked={groupAnnouncements} />
          </ListButton>
          <Spacer className="mt-auto" />
          {user.admin === 2 && (
            <ListButton highlighted onClick={() => setShowExportModal(true)} className="mr-1">
              Export Messages
            </ListButton>
          )}
        </>
      }
      floating={
        <ControlledModal
          noAutoFocus
          open={showExportModal}
          onOpenChange={(o) => !o && setShowExportModal(false)}
          className="!bg-charcoal"
          size="xs"
        >
          <ModalTitle hidden>Export Messages</ModalTitle>
          <ExportWidget className="!shadow-none" />
        </ControlledModal>
      }
    >
      <div className="flex flex-grow flex-col gap-4 overflow-auto bg-lightBlue p-4">
        <div className={`flex flex-col gap-2 rounded bg-white shadow ${tableLoading && tableData ? 'opacity-50' : ''}`}>
          {tableData ? (
            <LocalTable className="text-sm" columns={columns} data={tableData} search={search} pagination={0} />
          ) : (
            <Loader className="my-4" />
          )}
        </div>
        <div
          className={`flex flex-col gap-2 rounded bg-white p-4 shadow ${chartLoading && chartData ? 'opacity-50' : ''}`}
        >
          <b className="ml-4">Sent And Received Messages</b>
          {chartData ? (
            <LineChart className="min-h-72 px-2" data={chartData.inout} legend />
          ) : (
            <Loader className="my-4" />
          )}
          <b className="ml-4 mt-3">Breakdown By Type</b>
          {chartData ? (
            <LineChart className="min-h-72 px-2" data={chartData.usage} legend />
          ) : (
            <Loader className="my-4" />
          )}
        </div>
      </div>
    </ThreePanelLayout>
  )
}
